import React from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Switch, IconButton, Typography, Box, Tooltip, Skeleton
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

// CallLogPage 共用 sticky header style
const stickyHeaderSx = {
  bgcolor: '#ddd',
  fontWeight: 'bold',
  fontSize: '14px',
  position: 'sticky',
  top: 0,
  zIndex: 2,
  borderBottom: '1px solid #ccc',
};

// 限制類型 pill 配色（沿用 CallLog intent badge 的形狀）
const TYPE_PILL = {
  blackout:       { label: '完全不接受',  bg: '#fef0f0', border: '#f5c2c7', color: '#c62828' },
  closed:         { label: '當日店休',    bg: '#fff8e1', border: '#ffe082', color: '#e65100' },
  limited_hours:  { label: '部分時段不開放', bg: '#e3f2fd', border: '#90caf9', color: '#1565c0' },
};

// 共用 inline pill 樣式（CallLog 的 intent badge 形狀）
const PillSx = ({ bg, border, color }) => ({
  display: 'inline-block',
  bgcolor: bg,
  border: `1px solid ${border}`,
  color,
  fontSize: 12,
  px: 1,
  py: '2px',
  borderRadius: '12px',
  whiteSpace: 'nowrap',
});

const FALLBACK_LABELS = {
  human_callback: '回電真人',
  suggest_alt_date: '建議改日期',
};

const RECURRING_LABELS = {
  yearly_by_date: '每年同日',
  yearly_by_lunar: '每年同農曆',
};

const truncate = (s, n) => (s && s.length > n ? `${s.slice(0, n)}…` : s || '');

const formatDateRange = (start, end) => {
  if (!start || !end) return '—';
  if (start === end) return start;
  return `${start} ~ ${end}`;
};

const formatTimeRange = (allDay, start, end) => {
  if (allDay) return '整天';
  if (!start || !end) return '—';
  return `${start.slice(0, 5)} ~ ${end.slice(0, 5)}`;
};

// renderName：可選的名稱呈現函式（Plan 117）。訂單管理頁建的單日店休 name 是
// `closure-2026-08-18-all` 這種給程式辨識用的字串，呼叫端可以傳入自己的呈現方式。
// 預設維持 row.name —— 語音端的 ConstraintTab 行為完全不變。
const ConstraintTable = ({ rows, onEdit, onDelete, onToggle, busy, loading, renderName }) => {
  const COL_COUNT = 10;

  return (
    <TableContainer
      component={Paper}
      sx={{
        mt: 2,
        boxShadow: '0 2px 4px rgba(0,0,0,0.06)',
        maxHeight: 'calc(100vh - 320px)',
        overflow: 'auto',
      }}
    >
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell width="5%"  align="center" sx={stickyHeaderSx}>#</TableCell>
            <TableCell width="14%" sx={stickyHeaderSx}>名稱</TableCell>
            <TableCell width="12%" sx={stickyHeaderSx}>類型</TableCell>
            <TableCell width="14%" sx={stickyHeaderSx}>期間</TableCell>
            <TableCell width="10%" align="center" sx={stickyHeaderSx}>時段</TableCell>
            <TableCell width="9%"  align="center" sx={stickyHeaderSx}>重複</TableCell>
            <TableCell width="14%" sx={stickyHeaderSx}>訊息預覽</TableCell>
            <TableCell width="10%" align="center" sx={stickyHeaderSx}>後續處理</TableCell>
            <TableCell width="6%"  align="center" sx={stickyHeaderSx}>啟用</TableCell>
            <TableCell width="6%"  align="center" sx={stickyHeaderSx}>動作</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <TableRow key={`sk-${i}`}>
                {Array.from({ length: COL_COUNT }).map((_, j) => (
                  <TableCell key={j} sx={{ py: 1.75 }}><Skeleton variant="text" /></TableCell>
                ))}
              </TableRow>
            ))
          ) : !rows || rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={COL_COUNT} align="center" sx={{ py: 6, color: '#999' }}>
                <CalendarMonthIcon sx={{ fontSize: 48, color: '#ddd', mb: 1, display: 'block', mx: 'auto' }} />
                <Typography>暫無設定的限制 — 點右上「+ 新增限制」</Typography>
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row, idx) => {
              const type = TYPE_PILL[row.constraintType] || {
                label: row.constraintType, bg: '#f5f5f5', border: '#e0e0e0', color: '#555'
              };
              const dimmed = !row.enabled ? { opacity: 0.5 } : {};
              return (
                <TableRow
                  key={row.id}
                  hover
                  onClick={() => onEdit(row)}
                  sx={{
                    cursor: 'pointer',
                    bgcolor: idx % 2 === 0 ? 'white' : '#fffef5',
                    '&:hover': { bgcolor: '#f0f4ff !important' },
                    borderBottom: '1px solid #f0f0f0',
                  }}
                >
                  <TableCell align="center" sx={{ py: 1.75, fontSize: 14, ...dimmed }}>{idx + 1}</TableCell>
                  <TableCell sx={{ py: 1.75, fontSize: 14, fontWeight: 500, color: '#333', ...dimmed }}>
                    {renderName ? renderName(row) : row.name}
                  </TableCell>
                  <TableCell sx={{ py: 1.75, ...dimmed }}>
                    <Box component="span" sx={PillSx(type)}>{type.label}</Box>
                  </TableCell>
                  <TableCell sx={{ py: 1.75, fontSize: 14, color: '#555', ...dimmed }}>
                    {formatDateRange(row.startDate, row.endDate)}
                  </TableCell>
                  <TableCell align="center" sx={{ py: 1.75, fontSize: 14, color: '#555', ...dimmed }}>
                    {formatTimeRange(row.allDay, row.startTime, row.endTime)}
                  </TableCell>
                  <TableCell align="center" sx={{ py: 1.75, ...dimmed }}>
                    {row.recurring ? (
                      <Box component="span" sx={PillSx({ bg: '#f5f5f5', border: '#e0e0e0', color: '#555' })}>
                        {RECURRING_LABELS[row.recurring] || row.recurring}
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ color: '#ccc' }}>—</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ py: 1.75, ...dimmed }}>
                    <Tooltip title={row.messageText || ''} placement="top-start">
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200, color: '#666', fontSize: 13 }}>
                        {truncate(row.messageText, 24)}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center" sx={{ py: 1.75, fontSize: 13, color: '#555', ...dimmed }}>
                    {FALLBACK_LABELS[row.fallbackAction] || row.fallbackAction}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ py: 1.75 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Switch
                      size="small"
                      checked={!!row.enabled}
                      onChange={() => onToggle(row)}
                      disabled={busy}
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ py: 1.75 }} onClick={(e) => e.stopPropagation()}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                      <Tooltip title="編輯">
                        <IconButton
                          size="small"
                          onClick={() => onEdit(row)}
                          disabled={busy}
                          sx={{
                            width: 30, height: 30, borderRadius: '6px',
                            bgcolor: 'rgba(3,169,244,0.08)',
                            '&:hover': { bgcolor: 'rgba(3,169,244,0.18)' },
                          }}
                        >
                          <EditIcon sx={{ fontSize: 16, color: '#03a9f4' }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="刪除">
                        <IconButton
                          size="small"
                          onClick={() => onDelete(row)}
                          disabled={busy}
                          sx={{
                            width: 30, height: 30, borderRadius: '6px',
                            bgcolor: 'rgba(244,67,54,0.08)',
                            '&:hover': { bgcolor: 'rgba(244,67,54,0.18)' },
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: 16, color: '#e53935' }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ConstraintTable;
